/* eslint-disable */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Building2, Lock, Mail } from "lucide-react";

export default function Login() {
  const { login, verifyOtp, mfaChallenge, selectTenant } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [loading, setLoading] = useState(false);
  // Multi-tenant selection state — set when login() returns multi_tenant: true
  const [orgChoice, setOrgChoice] = useState(null); // { select_token, tenants: [...] }

  useEffect(() => {
    try {
      if (sessionStorage.getItem("hrms_inactivity_logout") === "1") {
        toast.warning(
          "You were signed out due to 30 minutes of inactivity. Please log in again.",
        );
        sessionStorage.removeItem("hrms_inactivity_logout");
      }
      const forced = sessionStorage.getItem("hrms_force_logout");
      if (forced) {
        toast.warning(forced, { duration: 6000 });
        sessionStorage.removeItem("hrms_force_logout");
      }
    } catch {
      /* ignore */
    }
  }, []);

  const routeAfterLogin = async (u) => {
    const userRoles = u.roles || (u.role ? [u.role] : []);
    let landing = "/dashboard";
    if (u.must_change_password) landing = "/change-password";
    else if (u.is_super_admin) landing = "/super-admin";
    else if (userRoles.includes("admin")) {
      try {
        const { data: s } = await (
          await import("../lib/api")
        ).default.get("/setup/status");
        if (!s.setup_completed) landing = "/setup";
      } catch (e) {
        /* ignore */
      }
    }
    nav(landing);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(email, password);
      if (r?.mfa) {
        toast.success("Code sent to your work email");
      } else if (r?.multi_tenant) {
        setOrgChoice({ select_token: r.select_token, tenants: r.tenants });
      } else {
        toast.success(`Welcome back, ${r.name}!`);
        await routeAfterLogin(r);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message, {
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  const onPickOrg = async (user_id, company_name) => {
    setLoading(true);
    try {
      const r = await selectTenant(orgChoice.select_token, user_id);
      if (r?.mfa) {
        toast.success("Code sent to your work email");
        setOrgChoice(null);
        return;
      }
      toast.success(`Signed in to ${company_name}`);
      setOrgChoice(null);
      await routeAfterLogin(r);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message, {
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verifyOtp(otp, rememberDevice);
      toast.success(`Welcome, ${u.name}!`);
      const userRoles = u.roles || (u.role ? [u.role] : []);
      let landing = "/dashboard";
      if (u.must_change_password) landing = "/change-password";
      else if (u.is_super_admin) landing = "/super-admin";
      else if (userRoles.includes("admin")) {
        try {
          const { data: s } = await (
            await import("../lib/api")
          ).default.get("/setup/status");
          if (!s.setup_completed) landing = "/setup";
        } catch (e) {
          /* ignore */
        }
      }
      nav(landing);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2 bg-white"
      data-testid="login-page"
    >
      {/* Left side - form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-lg bg-[#3D4A8E] flex items-center justify-center overflow-hidden p-1">
              <img
                src="/lanvaus-logo.png"
                alt="Lanvaus"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className="font-heading font-bold text-lg tracking-tight text-slate-900">
                LANVAUS
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                HRMS Portal
              </div>
            </div>
          </div>

          <div className="mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-3">
              Welcome back
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Sign in to your
              <br />
              Lanvaus account
            </h1>
            <p className="text-slate-500 mt-4 text-base">
              Manage your team, attendance and payroll — all in one place.
            </p>
          </div>

          <form
            onSubmit={mfaChallenge ? onVerify : onSubmit}
            className="space-y-5"
            data-testid="login-form"
          >
            {orgChoice ? (
              <div className="space-y-4" data-testid="org-picker">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
                  We found <b>{orgChoice.tenants.length}</b> organizations
                  linked to <b>{email}</b>. Pick the one you want to sign in to.
                </div>
                <div className="space-y-2">
                  {orgChoice.tenants.map((t) => (
                    <button
                      key={t.user_id}
                      type="button"
                      disabled={loading}
                      onClick={() => onPickOrg(t.user_id, t.company_name)}
                      data-testid={`org-pick-${t.tenant_id}`}
                      className="w-full text-left flex items-center gap-3 p-4 border border-slate-200 hover:border-[#5C6BC0] hover:bg-slate-50 rounded-xl transition disabled:opacity-50"
                    >
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {t.logo_url ? (
                          <img
                            src={t.logo_url}
                            alt={t.company_name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Building2 className="w-6 h-6 text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate">
                          {t.company_name}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                          <span
                            className={`inline-block w-1.5 h-1.5 rounded-full ${t.status === "Active" ? "bg-emerald-500" : "bg-slate-400"}`}
                          ></span>
                          {t.status}
                          {t.employee_id && (
                            <span className="font-mono">· {t.employee_id}</span>
                          )}
                          {t.role && (
                            <span className="capitalize">
                              · {t.role.replace("_", " ")}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setOrgChoice(null)}
                  data-testid="org-pick-back"
                  className="text-xs text-slate-500 hover:text-slate-700 underline mt-2"
                >
                  ← Use a different email
                </button>
              </div>
            ) : mfaChallenge ? (
              <>
                <div
                  className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-900"
                  data-testid="mfa-info"
                >
                  We&apos;ve sent a 6-digit verification code to{" "}
                  <b>{mfaChallenge.email}</b>. It expires in 10 minutes.
                </div>
                <div>
                  <Label
                    htmlFor="otp"
                    className="text-xs font-bold uppercase tracking-wider text-slate-600"
                  >
                    Verification code
                  </Label>
                  <Input
                    id="otp"
                    data-testid="mfa-otp-input"
                    required
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    maxLength={6}
                    placeholder="••••••"
                    autoFocus
                    className="mt-2 h-14 rounded-xl text-center text-2xl tracking-[0.5em] font-bold border-slate-200 focus:border-[#5C6BC0] focus:ring-2 focus:ring-[#5C6BC0]/20"
                  />
                </div>
                <label
                  htmlFor="rememberDevice"
                  className="flex items-start gap-3 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl p-3"
                  data-testid="remember-device-wrap"
                >
                  <input
                    id="rememberDevice"
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded text-[#3D4A8E] focus:ring-[#3D4A8E]"
                    data-testid="remember-device-checkbox"
                  />
                  <div className="text-sm leading-tight">
                    <div className="font-semibold text-slate-800">
                      Trust this device for 3 days
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Skip the verification code on this browser until{" "}
                      {new Date(
                        Date.now() + 3 * 86400 * 1000,
                      ).toLocaleDateString("en-IN", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                      .
                    </div>
                  </div>
                </label>
                <Button
                  type="submit"
                  data-testid="verify-otp-button"
                  disabled={loading || otp.length !== 6}
                  className="w-full h-12 rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white font-medium"
                >
                  {loading ? "Verifying…" : "Verify & sign in"}
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Label
                    htmlFor="email"
                    className="text-xs font-bold uppercase tracking-wider text-slate-600"
                  >
                    Email
                  </Label>
                  <div className="relative mt-2">
                    <Mail
                      className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                      strokeWidth={1.8}
                    />
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-11 h-12 rounded-xl border-slate-200 focus:border-[#5C6BC0] focus:ring-2 focus:ring-[#5C6BC0]/20"
                      placeholder="you@lanvaus.com"
                    />
                  </div>
                </div>
                <div>
                  <Label
                    htmlFor="password"
                    className="text-xs font-bold uppercase tracking-wider text-slate-600"
                  >
                    Password
                  </Label>
                  <div className="relative mt-2">
                    <Lock
                      className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                      strokeWidth={1.8}
                    />
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-11 h-12 rounded-xl border-slate-200 focus:border-[#5C6BC0] focus:ring-2 focus:ring-[#5C6BC0]/20"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loading}
                  className="w-full h-12 rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white font-medium"
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
                <div className="text-center">
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-sm text-[#3D4A8E] hover:underline"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </>
            )}
          </form>

          {/* <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-600 leading-relaxed">
            <div className="font-semibold text-slate-700 mb-1">Demo accounts</div>
            <div>Admin: <span className="font-mono">admin@lanvaus.com</span></div>
            <div>HR: <span className="font-mono">hr@lanvaus.com</span></div>
            <div>Employee: <span className="font-mono">arjun@lanvaus.com</span></div>
            <div className="mt-1">Password (all): <span className="font-mono">Lanvaus@2026</span></div>
          </div> */}
        </div>
      </div>

      {/* Right side - brand panel */}
      <div className="hidden lg:flex relative bg-[#3D4A8E] overflow-hidden">
        <div className="absolute inset-0 brand-grid-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#283593]/40" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-white/60">
            Built for Lanvaus Private Limited
          </div>
          <div>
            <div className="font-heading text-5xl font-bold leading-[1.05] tracking-tight">
              Your people,
              <br />
              <span className="text-white/70">organized.</span>
            </div>
            <p className="mt-6 text-white/70 text-base max-w-md leading-relaxed">
              Track attendance, manage leaves, generate payslips and keep your
              entire team aligned — from one minimal control room.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { k: "Modules", v: "5" },
                { k: "Roles", v: "3" },
                { k: "Realtime", v: "✓" },
              ].map((s) => (
                <div
                  key={s.k}
                  className="bg-white/10 backdrop-blur rounded-2xl p-4 border border-white/10"
                >
                  <div className="text-2xl font-heading font-bold">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">
                    {s.k}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-white/40">
            &copy; {new Date().getFullYear()} Lanvaus Private Limited
          </div>
        </div>
      </div>
    </div>
  );
}
